import { useMemo } from 'react';
import { useSearch } from '@tanstack/react-router';
import type { DiagramViewDefinition } from '@/hooks/useDiagramInfo';
import type { OrgModelLensProjection } from '../models/OrgModelProjection';
import { isFullOrgLiveView } from '../models/orgArtifactKind';
import { useOrgModel } from './useOrgModel';

export interface AtlasDepartmentViewTitle {
  heading: string;
  subtitle: string;
}

export function resolveAtlasDepartmentViewTitle(input: {
  departmentKey: string | undefined;
  departments: OrgModelLensProjection['departments'] | undefined;
  canonicalTitle: string;
  isFullOrgLiveView: boolean;
}): AtlasDepartmentViewTitle | null {
  if (!input.departmentKey || !input.isFullOrgLiveView) return null;

  const department = input.departments?.find(
    (candidate) => candidate.key === input.departmentKey,
  );
  return {
    heading: department?.label ?? 'Department view',
    subtitle: `Department view of ${input.canonicalTitle}`,
  };
}

export function useAtlasDepartmentViewTitle(input: {
  diagramId: string | undefined;
  folderId: string | null | undefined;
  viewDefinition: DiagramViewDefinition | null | undefined;
  canonicalTitle: string;
}): AtlasDepartmentViewTitle | null {
  const search = useSearch({ strict: false }) as { dept?: string } | undefined;
  const departmentKey = typeof search?.dept === 'string' && search.dept.trim()
    ? search.dept
    : undefined;
  const fullOrgLiveView = isFullOrgLiveView(input.viewDefinition);
  const orgModel = useOrgModel({
    folderId: input.folderId,
    diagramId: input.diagramId,
    enabled: Boolean(departmentKey && fullOrgLiveView),
  });

  return useMemo(
    () => resolveAtlasDepartmentViewTitle({
      departmentKey,
      departments: orgModel.data?.lenses?.departments,
      canonicalTitle: input.canonicalTitle,
      isFullOrgLiveView: fullOrgLiveView,
    }),
    [departmentKey, fullOrgLiveView, input.canonicalTitle, orgModel.data?.lenses?.departments],
  );
}
